import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ClipboardList,
  ImageOff,
  PackageMinus,
  Search,
  ShoppingCart,
  Clock,
  CheckCircle2,
  ShieldAlert
} from "lucide-react";
import api from "../api/api";
import { useAuth } from "../auth/AuthProvider";
import { useDialog } from "../components/DialogProvider";
import RequestDeleteModal from "../modals/RequestDeleteModal";
import RequestEditModal from "../modals/RequestEditModal";
import { formatDateTime, nowLocalSql } from "../utils/datetime";
import { useAppSettings } from "../settings/AppSettingsProvider";
import { includesSearch } from "../utils/search";
import { getKategoriFromKode } from "../utils/productCode";
import MarketplaceImportPanel from "../components/MarketplaceImportPanel";

function ProductIcon({ imageUrl, alt }) {
  const [broken, setBroken] = useState(false);
  const apiBase = process.env.REACT_APP_API_BASE_URL || "";

  const src = useMemo(() => {
    if (!imageUrl) return "";
    const raw = String(imageUrl);
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    return `${apiBase}${raw}`;
  }, [apiBase, imageUrl]);

  if (!src || broken) {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-zinc-200 bg-zinc-50 text-zinc-400">
        <ImageOff className="h-4 w-4" aria-hidden="true" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="h-10 w-10 shrink-0 rounded-md border border-zinc-200 object-cover"
      loading="lazy"
      onError={() => setBroken(true)}
    />
  );
}

function toneBadgeClass(kind, value) {
  const v = String(value || "").trim();

  if (kind === "tujuan") {
    switch (v) {
      case "Defect":
        return "bg-rose-50 text-rose-700 ring-rose-600/20";
      case "Sampel":
        return "bg-violet-50 text-violet-700 ring-violet-600/20";
      case "Vendor":
        return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
      case "E-Commerce":
        return "bg-sky-50 text-sky-700 ring-sky-600/20";
      case "Manual":
      default:
        return "bg-zinc-50 text-zinc-700 ring-zinc-500/20";
    }
  }

  if (kind === "source") {
    switch (v) {
      case "Defect":
        return "bg-rose-50 text-rose-700 ring-rose-600/20";
      case "Shopee":
      case "Tokopedia":
        return "bg-amber-50 text-amber-700 ring-amber-600/20";
      case "Spare":
      case "Manual":
      default:
        return "bg-zinc-50 text-zinc-700 ring-zinc-500/20";
    }
  }

  return "bg-zinc-50 text-zinc-700 ring-zinc-500/20";
}

function ApprovalBadge({ status }) {
  const value = String(status || "").trim().toUpperCase();
  if (value === "PENDING") return <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20"><Clock className="h-3 w-3" /> Menunggu</span>;
  if (value === "APPROVED") return <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20"><CheckCircle2 className="h-3 w-3" /> Disetujui</span>;
  if (value === "REJECTED") return <span className="inline-flex items-center gap-1.5 rounded-md bg-rose-50 px-2 py-1 text-[11px] font-medium text-rose-700 ring-1 ring-inset ring-rose-600/20"><ShieldAlert className="h-3 w-3" /> Ditolak</span>;
  return <span className="inline-flex items-center rounded-md bg-zinc-50 px-2 py-1 text-[11px] font-medium text-zinc-600 ring-1 ring-inset ring-zinc-500/20">-</span>;
}

function KpiCard({ title, value, icon: Icon }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm flex flex-col justify-between group hover:border-zinc-300 transition-colors">
      <div className="flex items-center justify-between">
        <div className="text-[13px] font-medium text-zinc-500 group-hover:text-zinc-700 transition-colors">{title}</div>
        {Icon && <Icon className="h-4 w-4 text-zinc-400 group-hover:text-zinc-600 transition-colors" strokeWidth={2} />}
      </div>
      <div className="mt-3 text-3xl font-semibold tracking-tight text-zinc-900 tabular-nums">{value}</div>
    </div>
  );
}

function DetailTypeBadge({ type }) {
  const value = String(type || "").trim().toUpperCase();
  if (value === "PRODUCT") return <span className="inline-flex items-center rounded-md bg-sky-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-700 ring-1 ring-inset ring-sky-600/10">Produk</span>;
  if (value === "BUNDLE") return <span className="inline-flex items-center rounded-md bg-violet-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-700 ring-1 ring-inset ring-violet-600/10">Bundle</span>;
  return <span className="inline-flex items-center rounded-md bg-zinc-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-zinc-700 ring-1 ring-inset ring-zinc-500/10">Detail</span>;
}

function isProdukUtama(kode) {
  return getKategoriFromKode(kode) === "PRODUK UTAMA";
}

function StockOut() {
  const dialog = useDialog();
  const auth = useAuth();
  const { settings } = useAppSettings();
  const lowStockThreshold = (() => {
    const n = Math.trunc(Number(settings?.lowStockThreshold ?? 5));
    return Number.isFinite(n) && n > 0 ? n : 5;
  })();

  const [products, setProducts] = useState([]);
  const [stockOutRows, setStockOutRows] = useState([]);
  const [bundles, setBundles] = useState([]); 
  const [cart, setCart] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedStockOutId, setExpandedStockOutId] = useState(null);
  const [detailRowsById, setDetailRowsById] = useState({});
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  const [detailErrorById, setDetailErrorById] = useState({});

  const [productQuery, setProductQuery] = useState("");
  const [productOpen, setProductOpen] = useState(false);
  const blurTimerRef = useRef(null);

  const [stockInfoByKode, setStockInfoByKode] = useState({}); 

  const [hoveredKode, setHoveredKode] = useState("");

  const [bundleItems, setBundleItems] = useState([]); 
  const [disableMultiply, setDisableMultiply] = useState(false);

  const [form, setForm] = useState({
    product_id: "",
    qty: 1,
    bundle_id: "",
    order_number: "",
    resi_number: "",
    tujuan: "E-Commerce",
    source: "Shopee",
    keterangan: "",
  });

  const isEcommerceTujuan = String(form.tujuan || "") === "E-Commerce";
  const isVendorTujuan = String(form.tujuan || "") === "Vendor";
  const sourceNormalized = (() => {
    const v = String(form.source || "").trim();
    if (!v) return "Spare";
    if (v === "Manual") return "Spare";
    return v;
  })();
  const isSpareSource = sourceNormalized === "Spare";
  const shouldRequireOrderNumber = isEcommerceTujuan;
  const shouldRequireKeterangan = isSpareSource || isVendorTujuan;
  const canUseBundle = !isVendorTujuan;

  const stockKey = (kode_barang) => {
    const kode = String(kode_barang || "").trim();
    if (!kode) return "";
    return `${sourceNormalized}|${kode}`;
  };

  const fetchProducts = async () => {
    const res = await api.get("/products");
    setProducts(res.data || []);
  };

  const fetchBundles = async () => {
    const res = await api.get("/bom");
    const rows = res.data || [];
    const map = new Map();
    for (const r of rows) {
      if (r?.bundle_id == null) continue;
      if (!map.has(r.bundle_id)) {
        map.set(r.bundle_id, {
          id: r.bundle_id,
          name: r.bundle_name,
          items: [],
        });
      }

      if (r.product_id != null) {
        map.get(r.bundle_id).items.push({
          product_id: Number(r.product_id),
          kode_barang: r.kode_barang,
          nama_barang: r.nama_barang,
          image_url: r.image_url,
          qty: Number(r.item_qty) || 0,
        });
      }
    }
    setBundles(Array.from(map.values()));
  };

  const fetchStockOut = async () => {
    const res = await api.get("/stock-out?limit=100");
    setStockOutRows(res.data || []);
  };

  const fetchStockOutDetail = async (stockOutId) => {
    const id = Number(stockOutId);
    if (!Number.isInteger(id) || id <= 0) return [];
    const res = await api.get(`/stock-out/${id}`);
    return Array.isArray(res.data) ? res.data : [];
  };

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([fetchProducts(), fetchBundles(), fetchStockOut()]);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const selectedProduct = useMemo(() => {
    const id = Number(form.product_id);
    if (!id) return null;
    return products.find((p) => Number(p.id) === id) || null;
  }, [products, form.product_id]);

  const productMatches = useMemo(() => {
    const q = String(productQuery || "").trim();
    if (!q) return [];
    const rows = products.filter((p) => {
      const kode = String(p?.kode_barang || "");
      const nama = String(p?.nama_barang || "");
      return includesSearch(`${kode} ${nama}`, q);
    });
    return rows.slice(0, 12);
  }, [products, productQuery]);

  const chooseProduct = (p) => {
    if (!p) return;
    setForm((f) => ({ ...f, product_id: String(p.id) }));
    setProductQuery("");
    setProductOpen(false);
  };

  const selectedProductKode = useMemo(() => {
    return selectedProduct?.kode_barang ? String(selectedProduct.kode_barang) : "";
  }, [selectedProduct]);

  const fetchStockAvailable = async (kode_barang) => {
    const kode = String(kode_barang || "").trim();
    if (!kode) return;
    const res = await api.get(
      `/stock-available?kode_barang=${encodeURIComponent(kode)}&source=${encodeURIComponent(sourceNormalized)}`
    );
    const stok = Number(res?.data?.stok);
    const key = stockKey(kode);
    if (!key) return;
    setStockInfoByKode((prev) => ({
      ...prev,
      [key]: {
        stok: Number.isFinite(stok) ? Math.trunc(stok) : 0,
        fetchedAt: Date.now(),
      },
    }));
  };

  const ensureStockFresh = async (kode_barang) => {
    const kode = String(kode_barang || "").trim();
    if (!kode) return;
    const cached = stockInfoByKode[stockKey(kode)];
    const age = cached?.fetchedAt ? Date.now() - cached.fetchedAt : Infinity;
    if (age < 10_000) return;
    await fetchStockAvailable(kode);
  };

  useEffect(() => {
    if (!selectedProductKode) return;
    const cached = stockInfoByKode[stockKey(selectedProductKode)];
    const age = cached?.fetchedAt ? Date.now() - cached.fetchedAt : Infinity;
    if (age < 10_000) return;

    fetchStockAvailable(selectedProductKode).catch((e) => {
      console.error(e);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductKode, sourceNormalized]);

  const totals = useMemo(() => {
    const mainItems = cart.filter((it) => isProdukUtama(it.kode_barang));
    const total_item = mainItems.length;
    const total_qty = mainItems.reduce((sum, it) => sum + (Number(it.qty) || 0), 0);
    return { total_item, total_qty };
  }, [cart]);

  const selectedBundle = useMemo(() => {
    const id = Number(form.bundle_id);
    if (!id) return null;
    return bundles.find((b) => Number(b.id) === id) || null;
  }, [bundles, form.bundle_id]);

  const bundleMultiplier = useMemo(() => {
    if (disableMultiply) return 1;
    return Math.max(1, Math.trunc(totals.total_qty || 0));
  }, [totals.total_qty, disableMultiply]);

  useEffect(() => {
    if (!selectedBundle) {
      setBundleItems([]);
      setDisableMultiply(false);
      return;
    }
  }, [selectedBundle]);

  useEffect(() => {
    if (!selectedBundle) {
      setBundleItems([]);
      return;
    }

    const multiplier = disableMultiply
      ? 1
      : Math.max(1, Math.trunc(totals.total_qty || 0));

    const newItems = (selectedBundle.items || []).map((it) => ({
      product_id: Number(it.product_id),
      kode_barang: it.kode_barang,
      nama_barang: it.nama_barang,
      qty: Math.trunc((Number(it.qty) || 0) * multiplier),
    }));

    setBundleItems(newItems);
  }, [selectedBundle, totals.total_qty, disableMultiply]);

  const updateEditingQty = (productId, nextQty) => {
    const qty = Math.max(0, Math.trunc(Number(nextQty) || 0));
    setBundleItems((prev) =>
      prev.map((x) => Number(x.product_id) === Number(productId) ? { ...x, qty } : x))
  };

  const buildRequiredQtyByKode = () => {
    const required = new Map();
    const add = (kodeBarang, qty) => {
      const kode = String(kodeBarang || "").trim();
      const n = Math.trunc(Number(qty) || 0);
      if (!kode || n <= 0) return;
      required.set(kode, (required.get(kode) || 0) + n);
    };

    cart.forEach((it) => add(it.kode_barang, it.qty));
    if (form.bundle_id) {
      bundleItems.forEach((it) => add(it.kode_barang, it.qty));
    }

    return required;
  };

  const validateSourceStock = async () => {
    const required = buildRequiredQtyByKode();
    if (required.size === 0) return true;

    const checks = await Promise.all(
      Array.from(required.keys()).map(async (kode) => {
        const res = await api.get(
          `/stock-available?kode_barang=${encodeURIComponent(kode)}&source=${encodeURIComponent(sourceNormalized)}`
        );
        const stok = Number(res?.data?.stok);
        const available = Number.isFinite(stok) ? Math.trunc(stok) : 0;
        const key = stockKey(kode);

        if (key) {
          setStockInfoByKode((prev) => ({
            ...prev,
            [key]: { stok: available, fetchedAt: Date.now() },
          }));
        }

        return { kode, available, need: required.get(kode) || 0 };
      })
    );

    const failed = checks.find((x) => x.available < x.need);
    if (failed) {
      dialog.toast({
        type: "error",
        title: "Stok source tidak mencukupi",
        message: `${failed.kode}: tersedia ${failed.available}, butuh ${failed.need}.`,
      });
      return false;
    }

    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: name === "qty" ? (value === "" ? "" : Math.trunc(Number(value))) : value,
    });
  };

  useEffect(() => {
    if (isEcommerceTujuan) return;
    setForm((prev) => {
      if (!prev.order_number && !prev.resi_number) return prev;
      return { ...prev, order_number: "", resi_number: "" };
    });
  }, [isEcommerceTujuan]);

  useEffect(() => {
    if (!isVendorTujuan) return;
    setForm((prev) => {
      if (!prev.bundle_id) return prev;
      return { ...prev, bundle_id: "" };
    });
    setBundleItems([]);
    setDisableMultiply(false);
  }, [isVendorTujuan]);

  const addToCart = async (e) => {
    e.preventDefault();

    const p = selectedProduct;
    const qty = Number(form.qty);
    if (!p || !Number.isFinite(qty) || qty <= 0) return;

    const kode = String(p.kode_barang || "").trim();
    const available = kode ? (Number(stockInfoByKode[stockKey(kode)]?.stok) || 0) : null;
    const currentInCart = cart.find((x) => String(x.kode_barang) === kode);
    const nextCartQty = (Number(currentInCart?.qty) || 0) + Math.trunc(qty);
    if (available != null && nextCartQty > available) {
      dialog.toast({
        type: "error",
        title: "Stok tidak cukup",
        message: `Stok ${kode} tersedia: ${available}. Di cart akan jadi: ${nextCartQty}.`,
      });
      return;
    }

    setCart((prev) => {
      const next = [...prev];
      const idx = next.findIndex((x) => Number(x.product_id) === Number(p.id));
      if (idx >= 0) {
        next[idx] = {
          ...next[idx],
          qty: (Number(next[idx].qty) || 0) + Math.trunc(qty),
        };
        return next;
      }

      next.push({
        product_id: Number(p.id),
        kode_barang: p.kode_barang,
        nama_barang: p.nama_barang,
        image_url: p.image_url,
        qty: Math.trunc(qty),
      });
      return next;
    });

    setForm((f) => ({ ...f, product_id: "", qty: 1 }));
    setProductQuery("");
    setProductOpen(false);
  };

  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((x) => Number(x.product_id) !== Number(productId)));
  };

  const clearCart = async () => {
    if (cart.length === 0) return;
    const ok = await dialog.confirm(
      "Kosongkan cart?\n\nIni diperlukan kalau kamu mau ganti Nomor Pesanan/Resi/Tujuan/Source/Bundle/Keterangan untuk transaksi baru.",
      { title: "Kosongkan Cart", confirmText: "Kosongkan", cancelText: "Batal" }
    );
    if (!ok) return;
    setCart([]);
  };

  const toggleStockOutDetail = async (item) => {
    const id = Number(item?.id);
    if (!Number.isInteger(id) || id <= 0) return;

    if (expandedStockOutId === id) {
      setExpandedStockOutId(null);
      return;
    }

    setExpandedStockOutId(id);
    if (detailRowsById[id]) return;

    try {
      setDetailLoadingId(id);
      setDetailErrorById((prev) => ({ ...prev, [id]: "" }));
      const rows = await fetchStockOutDetail(id);
      setDetailRowsById((prev) => ({ ...prev, [id]: rows }));
    } catch (e) {
      setDetailErrorById((prev) => ({
        ...prev,
        [id]: e?.response?.data?.message || e.message || "Gagal memuat detail stock keluar.",
      }));
    } finally {
      setDetailLoadingId((current) => (current === id ? null : current));
    }
  };

  const handleCheckout = async () => {
    if (cart.length === 0) return;

    const orderNumberVal = String(form.order_number || "").trim();
    if (shouldRequireOrderNumber && !orderNumberVal) {
      dialog.toast({ type: "error", title: "Nomor Pesanan", message: "Nomor pesanan wajib diisi sebelum checkout." });
      return;
    }

    const ketVal = String(form.keterangan || "").trim();
    if (shouldRequireKeterangan && !ketVal) {
      dialog.toast({
        type: "error",
        title: "Keterangan",
        message: isVendorTujuan
          ? "Untuk tujuan Vendor, isi nama vendor atau alasan barang keluar."
          : "Untuk memakai stok Spare, keterangan alasan wajib diisi.",
      });
      return;
    }

    const spareApproved = sourceNormalized === "Spare"
      ? await dialog.confirm(
          "Source Spare adalah stok cadangan untuk jaga-jaga.\n\nBarang dari Spare sebaiknya tidak dikeluarkan untuk penjualan normal karena bisa membuat stok pengaman habis. Lanjutkan hanya jika ini kebutuhan khusus dan alasannya sudah jelas di keterangan.",
          { title: "Pakai Stok Spare?", confirmText: "Ya, Pakai Spare", cancelText: "Batal" }
        )
      : false;
    if (sourceNormalized === "Spare" && !spareApproved) return;

    const resiVal = String(form.resi_number || "").trim();

    try {
      const sourceOk = await validateSourceStock();
      if (!sourceOk) return;
    } catch (e) {
      dialog.toast({
        type: "error",
        title: "Stok source tidak mencukupi",
        message: e?.response?.data?.message || e.message || "Gagal validasi stok source.",
      });
      return;
    }

    const lines = cart
      .map((it) => `${it.kode_barang} - ${it.nama_barang} x${it.qty}`)
      .join("\n");

    const bundle = canUseBundle ? selectedBundle : null;

    const orderSection = isEcommerceTujuan
      ? `Nomor Pesanan: ${orderNumberVal}\nResi: ${resiVal || "-"}`
      : "Nomor Pesanan: Auto fallback ORD-*\nResi: -";

    const ok = await dialog.confirm(
      `Yakin mau proses Stock Keluar (POS)?\n\n${orderSection}\n\n${lines}\n\nItem produk utama: ${totals.total_item}\nQty produk utama: ${totals.total_qty}\nTujuan: ${form.tujuan}\nSource: ${form.source}${bundle ? `\nBundle: ${bundle.name} (#${bundle.id})` : ""}${form.keterangan ? `\nKeterangan: ${form.keterangan}` : ""}`,
      { title: "Checkout", confirmText: "Keluarkan", cancelText: "Batal" }
    );
    if (!ok) return;

    try {
      setLoading(true);
      setError("");

      const payload = {
        items: cart.map((it) => ({ product_id: it.product_id, qty: it.qty })),
        order_number: isEcommerceTujuan ? orderNumberVal : null,
        resi_number: isEcommerceTujuan ? (resiVal || null) : null,
        bundle_id: canUseBundle && form.bundle_id ? Number(form.bundle_id) : null,
        bundle_items:
          canUseBundle && form.bundle_id
            ? bundleItems.map((x) => ({ product_id: x.product_id, qty: x.qty, }))
            : undefined,
          disableMultiply,
        tujuan: form.tujuan,
        source: form.source,
        keterangan: form.keterangan,
        allow_spare_out: spareApproved,
        tanggal: nowLocalSql(),
      };

      await dialog.toastPromise(
        api.post("/api/orders/checkout", payload),
        {
          loading: "Memproses checkout…",
          success: (r) => {
            const num = r?.data?.order?.order_number;
            const code = r?.data?.order?.order_code;
            return num ? `Checkout berhasil: ${num}` : code ? `Checkout berhasil: ${code}` : "Checkout berhasil";
          },
          error: (e2) => e2?.response?.data?.message || e2.message || "Checkout gagal diproses.",
        },
        { successTitle: "Berhasil", errorTitle: "Gagal" }
      );

      setCart([]);
      setForm((f) => ({ ...f, product_id: "", qty: 1, keterangan: "", bundle_id: "", order_number: "", resi_number: "" }));
      setProductQuery("");
      setProductOpen(false);
      setBundleItems([]);
      await fetchStockOut();

      const kodeList = Array.from(new Set(cart.map((x) => String(x.kode_barang || "").trim()).filter(Boolean)));
      for (const k of kodeList) {
        fetchStockAvailable(k).catch(() => {});
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Terjadi error";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const requestEdit = async (item) => {
    if (!item?.id) return;
    while (true) {
      const valueRef = { current: null };
      const ok = await dialog.custom(
        <RequestEditModal item={item} valueRef={valueRef} />,
        { title: "Request Edit Stock Keluar", confirmText: "Kirim", cancelText: "Batal" }
      );
      if (!ok) return;

      const payload = valueRef.current || {};
      const reason = String(payload.reason || "").trim();
      if (!reason) {
        await dialog.alert("Alasan perubahan wajib diisi.", { title: "Wajib diisi" });
        continue;
      }

      try {
        setLoading(true);
        await dialog.toastPromise(
          api.post("/approval/request-edit", {
            module: "stock_out",
            target_id: item.id,
            new_data: { qty: payload.qty, keterangan: payload.keterangan },
            reason,
          }),
          {
            loading: "Mengirim request...",
            success: "Request edit terkirim.",
            error: (e2) => e2?.response?.data?.message || e2.message || "Gagal mengirim request.",
          },
          { successTitle: "Terkirim", errorTitle: "Gagal" }
        );
        await fetchStockOut();
      } finally {
        setLoading(false);
      }
      return;
    }
  };

  const requestDelete = async (item) => {
    if (!item?.id) return;
    while (true) {
      const reasonRef = { current: "" };
      const ok = await dialog.custom(
        <RequestDeleteModal item={item} reasonRef={reasonRef} />,
        { title: "Request Hapus Stock Keluar", confirmText: "Kirim", cancelText: "Batal" }
      );
      if (!ok) return;

      const reason = String(reasonRef.current || "").trim();
      if (!reason) {
        await dialog.alert("Alasan penghapusan wajib diisi.", { title: "Wajib diisi" });
        continue;
      }

      try {
        setLoading(true);
        await dialog.toastPromise(
          api.post("/approval/request-delete", {
            module: "stock_out",
            target_id: item.id,
            reason,
          }),
          {
            loading: "Mengirim request...",
            success: "Request hapus terkirim.",
            error: (e2) => e2?.response?.data?.message || e2.message || "Gagal mengirim request.",
          },
          { successTitle: "Terkirim", errorTitle: "Gagal" }
        );
        await fetchStockOut();
      } finally {
        setLoading(false);
      }
      return;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10 max-w-[1400px]">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Stock Keluar (POS)</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Proses checkout barang keluar untuk e-commerce, manual, defect, atau penjualan bundel.
        </p>
      </header>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-[13px] text-red-600">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard icon={ShoppingCart} title="Produk Utama di Cart" value={totals.total_item} />
        <KpiCard icon={PackageMinus} title="Qty Utama di Cart" value={totals.total_qty} />
        <KpiCard icon={ClipboardList} title="Riwayat Tampil" value={stockOutRows.length} />
      </div>

      <MarketplaceImportPanel />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[400px_minmax(0,1fr)] items-start">
        
        {/* Left Column: Form */}
        <div className="space-y-6">
          <form onSubmit={addToCart} className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-zinc-900 mb-5 flex items-center gap-2">
              <PackageMinus className="h-4 w-4 text-zinc-400" />
              Tambah ke Cart
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Pilih Produk</label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
                  <input
                    value={productQuery}
                    onChange={(e) => {
                      setProductQuery(e.target.value);
                      setForm((f) => ({ ...f, product_id: "" }));
                      setProductOpen(true);
                    }}
                    onFocus={() => {
                      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
                      setProductOpen(true);
                    }}
                    onBlur={() => {
                      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
                      blurTimerRef.current = setTimeout(() => setProductOpen(false), 150);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && productMatches[0]) {
                        e.preventDefault();
                        chooseProduct(productMatches[0]);
                      }
                      if (e.key === "Escape") setProductOpen(false);
                    }}
                    className="w-full rounded-md border border-zinc-300 pl-9 pr-3 py-2 text-[13px] text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm"
                    placeholder="Ketik kode/nama produk"
                    autoComplete="off"
                  />

                  {productOpen && String(productQuery || "").trim() && (
                    <div className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md border border-zinc-200 bg-white shadow-lg">
                      {productMatches.length === 0 ? (
                        <div className="px-3 py-3 text-[13px] text-zinc-500 text-center">Tidak ada hasil</div>
                      ) : (
                        productMatches.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onMouseDown={(ev) => ev.preventDefault()}
                            onMouseEnter={() => {
                              const kode = String(p?.kode_barang || "").trim();
                              setHoveredKode(kode);
                              ensureStockFresh(kode).catch(() => {});
                            }}
                            onMouseLeave={() => setHoveredKode("")}
                            onClick={() => chooseProduct(p)}
                            className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-zinc-50 transition-colors border-b border-zinc-100 last:border-0"
                          >
                            <span className="min-w-0">
                              <span className="block truncate text-[13px] font-medium text-zinc-900">{p.nama_barang || "(Tanpa nama)"}</span>
                              <span className="block truncate font-mono text-[11px] text-zinc-500">{p.kode_barang}</span>
                            </span>
                            <span className="shrink-0 text-right ml-2">
                              {hoveredKode && hoveredKode === String(p?.kode_barang || "").trim() ? (
                                <span className="inline-flex rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-600 border border-zinc-200">
                                  Stok: {Number.isFinite(Number(stockInfoByKode[stockKey(hoveredKode)]?.stok)) ? Math.trunc(Number(stockInfoByKode[stockKey(hoveredKode)]?.stok)) : "…"}
                                </span>
                              ) : null}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {selectedProduct && (
                  <div className="mt-2 flex items-center gap-3 rounded-md border border-zinc-200 bg-zinc-50/50 p-2">
                    <ProductIcon imageUrl={selectedProduct.image_url} alt={selectedProduct.nama_barang || "Produk"} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-medium text-zinc-900">{selectedProduct.nama_barang || "(Tanpa nama)"}</div>
                      <div className="font-mono text-[11px] text-zinc-500 mt-0.5">{selectedProduct.kode_barang}</div>
                      {selectedProductKode && (() => {
                        const info = stockInfoByKode[stockKey(selectedProductKode)];
                        const stok = Number(info?.stok);
                        const safe = Number.isFinite(stok) ? stok : null;
                        const warnLow = safe != null && safe > 0 && safe <= lowStockThreshold;
                        const isZero = safe === 0;
                        const tone = isZero
                          ? "bg-rose-50 text-rose-700 border-rose-200"
                          : warnLow
                            ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-emerald-50 text-emerald-700 border-emerald-200";

                        return (
                          <div className="mt-1.5">
                            <span className={`inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${tone}`}>
                              {safe == null ? "Stok: …" : `Stok: ${safe}`}
                              {isZero ? " (Habis)" : warnLow ? " (Menipis)" : ""}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Qty (Jumlah)</label>
                <input
                  type="number"
                  name="qty"
                  value={form.qty}
                  onChange={handleChange}
                  className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm"
                  min="1"
                />
              </div>

              <button
                type="submit"
                disabled={(() => {
                  if (loading || !selectedProduct || !form.qty || Number(form.qty) <= 0) return true;
                  const kode = String(selectedProduct?.kode_barang || "").trim();
                  if (!kode) return false;
                  const available = Number(stockInfoByKode[stockKey(kode)]?.stok);
                  if (!Number.isFinite(available)) return false;
                  const currentInCart = cart.find((x) => String(x.kode_barang) === kode);
                  const nextCartQty = (Number(currentInCart?.qty) || 0) + Math.trunc(Number(form.qty) || 0);
                  return nextCartQty > available;
                })()}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-zinc-900 px-4 py-2.5 text-[13px] font-medium text-white shadow-sm transition hover:bg-zinc-800 focus:outline-none disabled:opacity-50"
              >
                Masukkan ke Cart
              </button>
            </div>
          </form>

          {/* Checkout Panel */}
          <div className="rounded-lg border border-zinc-200 bg-white shadow-sm overflow-hidden flex flex-col">
            <div className="bg-zinc-50/50 border-b border-zinc-200 p-4">
              <h2 className="text-sm font-semibold text-zinc-900 mb-1 flex items-center gap-2">
                <ShoppingCart className="h-4 w-4 text-zinc-400" />
                Cart & Checkout
              </h2>
              <div className="text-[11px] text-zinc-500">
                Item utama: {totals.total_item} | Qty utama: {totals.total_qty}
              </div>
            </div>

            <div className="p-4 space-y-4 flex-1">
              {cart.length === 0 ? (
                <div className="py-8 text-center border border-dashed border-zinc-300 rounded-md bg-zinc-50">
                  <ShoppingCart className="h-6 w-6 text-zinc-300 mx-auto mb-2" />
                  <div className="text-[13px] font-medium text-zinc-700">Cart masih kosong</div>
                  <div className="text-[11px] text-zinc-500 mt-1">Tambahkan produk dari form di atas.</div>
                </div>
              ) : (
                <div className="rounded-md border border-zinc-200 overflow-hidden">
                  <table className="min-w-full text-left text-[13px]">
                    <thead className="bg-zinc-50 border-b border-zinc-200">
                      <tr>
                        <th className="px-3 py-2 font-semibold text-zinc-700">Produk</th>
                        <th className="px-3 py-2 font-semibold text-zinc-700 text-right">Qty</th>
                        <th className="px-3 py-2 font-semibold text-zinc-700 text-right"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {cart.map((it) => (
                        <tr key={it.product_id} className="hover:bg-zinc-50/50">
                          <td className="px-3 py-2">
                            <div className="font-medium text-zinc-900 truncate max-w-[150px]">{it.nama_barang}</div>
                            <div className="font-mono text-[11px] text-zinc-500">{it.kode_barang}</div>
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-zinc-900">{it.qty}</td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => removeFromCart(it.product_id)}
                              className="text-[11px] font-medium text-rose-600 hover:text-rose-800"
                            >
                              Hapus
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {isEcommerceTujuan ? (
                <div className="space-y-3 pt-2">
                  <div>
                    <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">
                      Nomor Pesanan {shouldRequireOrderNumber ? <span className="text-rose-600">*</span> : null}
                    </label>
                    <input
                      name="order_number"
                      value={form.order_number}
                      onChange={handleChange}
                      className={`w-full rounded-md border px-3 py-2 text-[13px] text-zinc-900 focus:outline-none focus:ring-1 shadow-sm ${
                        shouldRequireOrderNumber && !String(form.order_number || "").trim() && cart.length > 0
                          ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500"
                          : "border-zinc-300 focus:border-zinc-900 focus:ring-zinc-900"
                      }`}
                      placeholder="Contoh: 0912308XYUHN"
                    />
                  </div>
                  <div>
                    <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Nomor Resi <span className="text-zinc-400 font-normal">(opsional)</span></label>
                    <input
                      name="resi_number"
                      value={form.resi_number}
                      onChange={handleChange}
                      className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm"
                      placeholder="Contoh: SPXID1231931"
                    />
                  </div>
                </div>
              ) : (
                <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-800">
                  Tujuan non e-commerce: Nomor Pesanan & Resi disembunyikan (otomatis ORD-*).
                </div>
              )}

              <div className="space-y-3 pt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Tujuan</label>
                    <select name="tujuan" value={form.tujuan} onChange={handleChange} className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm bg-white">
                      <option>E-Commerce</option>
                      <option>Manual</option>
                      <option>Defect</option>
                      <option>Sampel</option>
                      <option>Vendor</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Source</label>
                    <select name="source" value={form.source} onChange={handleChange} className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm bg-white">
                      <option>Spare</option>
                      <option>Shopee</option>
                      <option>Tokopedia</option>
                      <option>Defect</option>
                    </select>
                  </div>
                </div>

                {sourceNormalized === "Spare" && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800 leading-relaxed">
                    Source Spare adalah stok cadangan. Konfirmasi & alasan wajib saat checkout.
                  </div>
                )}
                {sourceNormalized === "Defect" && (
                  <div className="rounded-md border border-rose-200 bg-rose-50 p-2 text-[11px] text-rose-800 leading-relaxed">
                    Menggunakan stok defect (tidak memotong stok normal).
                  </div>
                )}

                {canUseBundle ? (
                  <div>
                    <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">Bundle / Komponen <span className="text-zinc-400 font-normal">(opsional)</span></label>
                    <select name="bundle_id" value={form.bundle_id} onChange={handleChange} className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm bg-white mb-2">
                      <option value="">Tanpa bundle</option>
                      {bundles.map((b) => (
                        <option key={b.id} value={b.id}>{b.name} (#{b.id})</option>
                      ))}
                    </select>

                    {selectedBundle && (
                      <div className="rounded-md border border-zinc-200 bg-zinc-50 overflow-hidden mt-2">
                        <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-100/50 flex justify-between items-center">
                          <span className="text-[11px] font-semibold text-zinc-700">Detail Komponen (x{bundleMultiplier})</span>
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input type="checkbox" checked={disableMultiply} onChange={(e) => setDisableMultiply(e.target.checked)} className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900" />
                            <span className="text-[10px] font-medium text-zinc-600">Matikan pengalian</span>
                          </label>
                        </div>
                        <div className="max-h-40 overflow-y-auto p-2">
                          {bundleItems.map((it) => (
                            <div key={it.product_id} className="flex items-center justify-between gap-2 text-[11px] py-1 border-b border-zinc-100 last:border-0">
                              <span className="truncate text-zinc-700">{it.nama_barang || it.kode_barang}</span>
                              <input type="number" min="0" value={it.qty} onChange={(e) => updateEditingQty(it.product_id, e.target.value)} className="w-16 rounded border border-zinc-300 px-1 py-0.5 text-right font-medium text-zinc-900 focus:border-zinc-900 focus:outline-none" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800">
                    Tujuan Vendor tidak menggunakan bundle komponen.
                  </div>
                )}

                <div>
                  <label className="block text-[13px] font-medium text-zinc-700 mb-1.5">
                    Keterangan {shouldRequireKeterangan ? <span className="text-rose-600">*</span> : null}
                  </label>
                  <textarea
                    name="keterangan"
                    value={form.keterangan}
                    onChange={handleChange}
                    placeholder={shouldRequireKeterangan ? "Wajib diisi..." : "Opsional..."}
                    className={`w-full rounded-md border px-3 py-2 text-[13px] text-zinc-900 focus:outline-none focus:ring-1 shadow-sm min-h-[60px] ${
                      shouldRequireKeterangan && !String(form.keterangan || "").trim() && cart.length > 0
                        ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500"
                        : "border-zinc-300 focus:border-zinc-900 focus:ring-zinc-900"
                    }`}
                  />
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-zinc-200 bg-zinc-50/50 flex gap-2">
              <button type="button" onClick={clearCart} disabled={loading || cart.length === 0} className="w-1/3 inline-flex items-center justify-center rounded-md border border-zinc-200 bg-white px-3 py-2 text-[13px] font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 focus:outline-none disabled:opacity-50 transition-colors">
                Kosongkan
              </button>
              <button type="button" onClick={handleCheckout} disabled={loading || cart.length === 0 || (shouldRequireOrderNumber && !String(form.order_number || "").trim()) || (shouldRequireKeterangan && !String(form.keterangan || "").trim())} className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-zinc-900 px-3 py-2 text-[13px] font-medium text-white shadow-sm transition hover:bg-zinc-800 focus:outline-none disabled:opacity-50">
                {loading ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/60 border-t-white" /> : <PackageMinus className="h-4 w-4" />}
                {loading ? "Memproses..." : "Checkout Stock Keluar"}
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: History */}
        <div className="rounded-lg border border-zinc-200 bg-white shadow-sm overflow-hidden flex flex-col h-full">
          <div className="border-b border-zinc-200 px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between bg-zinc-50/50 gap-2">
            <div>
              <h2 className="text-sm font-semibold text-zinc-900">Riwayat Stock Keluar</h2>
              <p className="text-[11px] text-zinc-500">Klik ikon panah untuk melihat detail komponen paket.</p>
            </div>
            <span className="inline-flex items-center justify-center rounded-full bg-zinc-200/50 px-2.5 py-0.5 text-[11px] font-semibold text-zinc-600 self-start sm:self-auto">
              {stockOutRows.length} transaksi
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-[13px]">
              <thead className="border-b border-zinc-200 bg-white">
                <tr>
                  <th className="w-10 px-3 py-3 text-center"></th>
                  <th className="px-4 py-3 font-medium text-zinc-500">Info Transaksi</th>
                  <th className="px-4 py-3 font-medium text-zinc-500">Tujuan & Source</th>
                  <th className="px-4 py-3 font-medium text-zinc-500">Status</th>
                  {!auth.isAdmin && <th className="px-4 py-3 font-medium text-zinc-500 text-right">Aksi</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {stockOutRows.length === 0 ? (
                  <tr>
                    <td colSpan={auth.isAdmin ? 4 : 5} className="px-5 py-12 text-center text-zinc-500 border-b-0">
                      <ClipboardList className="h-8 w-8 text-zinc-300 mx-auto mb-3" />
                      Belum ada riwayat stock keluar.
                    </td>
                  </tr>
                ) : (
                  stockOutRows.map((item) => {
                    const id = Number(item.id);
                    const expanded = expandedStockOutId === id;
                    const details = detailRowsById[item.id] || [];

                    return (
                      <Fragment key={item.id}>
                        <tr className={`hover:bg-zinc-50/50 transition-colors ${expanded ? "bg-zinc-50/50" : ""}`}>
                          <td className="px-3 py-3 text-center align-top pt-4">
                            <button
                              type="button"
                              onClick={() => toggleStockOutDetail(item)}
                              className={`inline-flex h-7 w-7 items-center justify-center rounded border ${expanded ? 'border-zinc-300 bg-zinc-100' : 'border-zinc-200 bg-white'} text-zinc-500 hover:border-zinc-300 hover:bg-zinc-50 transition-colors`}
                            >
                              <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
                            </button>
                          </td>
                          <td className="px-4 py-3 align-top">
                            <div className="flex items-start gap-3">
                              <div className="hidden sm:block mt-1">
                                <ProductIcon imageUrl={item.image_url} alt={item.nama_barang || "Produk"} />
                              </div>
                              <div>
                                <div className="text-[11px] text-zinc-500 mb-1">{formatDateTime(item.tanggal) || "-"}</div>
                                <div className="font-semibold text-zinc-900 flex flex-wrap items-center gap-1.5">
                                  {item.nama_barang || item.kode_barang}
                                  {item.detail_count > 1 && (
                                    <span className="inline-flex items-center rounded-md bg-indigo-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
                                      Paket ({item.detail_count})
                                    </span>
                                  )}
                                </div>
                                <div className="font-mono text-[11px] text-zinc-500 mt-0.5">
                                  {item.kode_barang} • <span className="font-semibold text-zinc-900">Qty: {item.qty}</span>
                                </div>
                                {item.keterangan && (
                                  <div className="mt-2 text-[11px] text-zinc-600 bg-white border border-zinc-100 px-2 py-1 rounded w-fit">
                                    {item.keterangan}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 align-top pt-5">
                            <div className="flex flex-col gap-1.5 items-start">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${toneBadgeClass("tujuan", item.tujuan)}`}>T: {item.tujuan || "-"}</span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${toneBadgeClass("source", item.source)}`}>S: {item.source || "-"}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 align-top pt-5">
                            <ApprovalBadge status={item.approval_status} />
                          </td>
                          {!auth.isAdmin && (
                            <td className="px-4 py-3 align-top text-right pt-5">
                              <div className="flex flex-col gap-1.5 items-end">
                                <button
                                  type="button"
                                  disabled={loading || item.approval_status === "PENDING"}
                                  onClick={() => requestEdit(item)}
                                  className="inline-flex items-center justify-center rounded border border-zinc-200 bg-white px-2 py-1 text-[10px] font-medium text-zinc-700 hover:bg-zinc-50 transition-colors disabled:opacity-50"
                                >
                                  Req. Edit
                                </button>
                                <button
                                  type="button"
                                  disabled={loading || item.approval_status === "PENDING"}
                                  onClick={() => requestDelete(item)}
                                  className="inline-flex items-center justify-center rounded border border-rose-200 bg-white px-2 py-1 text-[10px] font-medium text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-50"
                                >
                                  Req. Hapus
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                        {expanded && (
                          <tr className="bg-zinc-50/50">
                            <td colSpan={auth.isAdmin ? 4 : 5} className="px-10 py-4 border-b border-zinc-100">
                              <div className="rounded-md border border-zinc-200 bg-white overflow-hidden shadow-sm">
                                <div className="px-4 py-2 border-b border-zinc-200 bg-zinc-100/50 flex justify-between items-center">
                                  <span className="text-[11px] font-semibold text-zinc-700">Rincian Paket / Bundle Keluar</span>
                                  <span className="inline-flex items-center rounded-full bg-white border border-zinc-200 px-2 py-0.5 text-[10px] font-medium text-zinc-600">{details.length} item</span>
                                </div>
                                {detailLoadingId === id ? (
                                  <div className="p-4 text-[12px] text-zinc-500 text-center">Memuat detail komponen...</div>
                                ) : detailErrorById[item.id] ? (
                                  <div className="p-4 text-[12px] text-rose-600 text-center">{detailErrorById[item.id]}</div>
                                ) : details.length === 0 ? (
                                  <div className="p-4 text-[12px] text-zinc-500 text-center">Tidak ada rincian komponen (bukan paket).</div>
                                ) : (
                                  <table className="min-w-full text-[12px]">
                                    <thead className="bg-white border-b border-zinc-100">
                                      <tr>
                                        <th className="px-4 py-2 font-medium text-zinc-500">Komponen / Item</th>
                                        <th className="px-4 py-2 font-medium text-zinc-500">Tipe</th>
                                        <th className="px-4 py-2 font-medium text-zinc-500 text-right">Qty Keluar</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-zinc-50">
                                      {details.map((detail) => (
                                        <tr key={detail.id || `${detail.kode_barang}-${detail.qty}`} className="hover:bg-zinc-50/50">
                                          <td className="px-4 py-2.5">
                                            <div className="flex items-center gap-2.5">
                                              <ProductIcon imageUrl={detail.image_url} alt={detail.nama_barang || "Detail"} />
                                              <div>
                                                <div className="font-medium text-zinc-900 truncate max-w-[200px]">{detail.product_name || detail.nama_barang || detail.kode_barang}</div>
                                                <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{detail.kode_barang}</div>
                                              </div>
                                            </div>
                                          </td>
                                          <td className="px-4 py-2.5">
                                            <DetailTypeBadge type={detail.type} />
                                          </td>
                                          <td className="px-4 py-2.5 text-right font-semibold text-zinc-900">
                                            {detail.qty}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StockOut;
